<!--
  URL review queue: companies a URL-discovery run couldn't match to a website
  on its own. Left, the queue; right, the selected row's evidence and the
  decision. Deciding writes the company's website (or marks it as having none)
  straight away and moves on to the next row; Undo puts the company back.
  Claude works the same queue through the MCP tools, shown as source "ai".
-->
<template>
  <div class="w-full h-[calc(100vh-3.5rem)] flex flex-col bg-gray-50 text-sm">
    <!-- Filters -->
    <div class="flex items-end gap-3 flex-wrap px-4 py-2 border-b border-gray-300 bg-white">
      <div class="mr-2">
        <div class="flex items-center gap-1">
          <span class="font-semibold text-gray-800">URL Review</span>
          <button
            type="button"
            class="rounded-full hover:bg-gray-100 p-0.5"
            title="How this page works, and what its terms mean"
            aria-label="Help"
            @click="helpOpen = true"
          ><HelpSVG stroke="#4B5563" /></button>
        </div>
        <div class="text-xs text-gray-500">
          {{ countCapped ? `${count}+` : count }} {{ STATUS_LABELS[status].toLowerCase() }}
        </div>
      </div>
      <label class="flex flex-col gap-1">
        <span class="text-xs text-gray-500">Status</span>
        <select v-model="status" class="border border-gray-300 rounded-md px-2 py-1 bg-white">
          <option v-for="(label, key) in STATUS_LABELS" :key="key" :value="key">{{ label }}</option>
        </select>
      </label>
      <label class="flex flex-col gap-1">
        <span class="text-xs text-gray-500">Kind</span>
        <select v-model="kind" class="border border-gray-300 rounded-md px-2 py-1 bg-white">
          <option value="review">Needs review</option>
          <option value="no_match">No match found</option>
          <option value="">Both</option>
        </select>
      </label>
      <label class="flex flex-col gap-1">
        <span class="text-xs text-gray-500">Run</span>
        <select v-model="runId" class="border border-gray-300 rounded-md px-2 py-1 bg-white">
          <option value="">All runs</option>
          <option v-for="r in runs" :key="r.id" :value="String(r.id)">
            Run {{ r.id }} · {{ formatDay(r.started_at) }} · {{ r.pending }} pending
          </option>
        </select>
      </label>
      <label class="flex flex-col gap-1">
        <span class="text-xs text-gray-500">Run's confidence</span>
        <select v-model="band" class="border border-gray-300 rounded-md px-2 py-1 bg-white">
          <option value="">Any</option>
          <option value="68-">68 and up</option>
          <option value="50-67">50–67</option>
          <option value="-49">Under 50</option>
        </select>
      </label>
      <label class="flex flex-col gap-1">
        <span class="text-xs text-gray-500">CAGE code</span>
        <select v-model="hasCage" class="border border-gray-300 rounded-md px-2 py-1 bg-white">
          <option value="">Either</option>
          <option value="true">Has one</option>
          <option value="false">None</option>
        </select>
      </label>
      <label v-if="status === 'decided'" class="flex flex-col gap-1">
        <span class="text-xs text-gray-500">Decided by</span>
        <select v-model="source" class="border border-gray-300 rounded-md px-2 py-1 bg-white">
          <option value="">Anyone</option>
          <option value="human">A person</option>
          <option value="ai">Claude</option>
          <option value="spreadsheet">Spreadsheet</option>
        </select>
      </label>
      <label class="flex flex-col gap-1 grow min-w-[12rem]">
        <span class="text-xs text-gray-500">Search</span>
        <input
          v-model="search"
          type="search"
          placeholder="Company name or proposed site"
          class="border border-gray-300 rounded-md px-2 py-1"
        />
      </label>
      <!-- Keyboard shortcuts are off until turned on here, so typing on the
           page can never decide a row by accident. -->
      <button
        type="button"
        @click="shortcutsOn = !shortcutsOn"
        class="mb-0.5 px-2 py-1 rounded-md text-xs border"
        :class="shortcutsOn ? 'border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-100'"
        :aria-pressed="shortcutsOn"
      >Keyboard shortcuts: {{ shortcutsOn ? 'on' : 'off' }}</button>
      <!-- Decisions, skips and undos count toward an open research session,
           started from the right sidebar like Vet URLs. -->
      <div v-if="hasActiveResearchSession" class="mb-1 flex items-center whitespace-nowrap text-xs text-gray-600">
        Session active <span class="ml-1 h-3 w-3 animate-pulse rounded-full bg-red-500"></span>
      </div>
    </div>
    <div
      v-if="shortcutsOn"
      class="px-4 py-2 border-b border-gray-200 bg-white text-xs text-gray-600 flex flex-wrap gap-x-5 gap-y-1
             [&_kbd]:inline-block [&_kbd]:min-w-[1.25rem] [&_kbd]:px-1 [&_kbd]:mx-0.5 [&_kbd]:text-center
             [&_kbd]:font-mono [&_kbd]:text-gray-800 [&_kbd]:bg-gray-100 [&_kbd]:border [&_kbd]:border-gray-300
             [&_kbd]:border-b-2 [&_kbd]:rounded"
    >
      <span><kbd>j</kbd>/<kbd>k</kbd> next / previous row</span>
      <span><kbd>1</kbd>–<kbd>9</kbd> choose a candidate</span>
      <span><kbd>a</kbd> use the chosen site</span>
      <span><kbd>n</kbd> no website</span>
      <span><kbd>s</kbd> skip</span>
      <span><kbd>o</kbd> open the chosen site in a new tab</span>
      <span><kbd>g</kbd> Google the company's name and address</span>
      <span><kbd>p</kbd> toggle preview</span>
      <span><kbd>u</kbd> undo the last decision</span>
    </div>

    <div class="flex flex-1 min-h-0">
      <!-- Queue -->
      <div class="w-[340px] min-w-[340px] border-r border-gray-300 bg-white overflow-y-auto">
        <div v-if="loading && !rows.length" class="text-gray-500 py-12 text-center">Loading…</div>
        <div v-else-if="!rows.length" class="text-gray-500 py-12 text-center px-4">
          {{ status === 'pending' ? 'Nothing waiting for review here.' : 'Nothing matches these filters.' }}
        </div>
        <div
          v-for="row in rows"
          :key="row.id"
          :ref="(el) => { if (el) rowEls[row.id] = el }"
          class="px-3 py-2 border-b border-gray-200 cursor-pointer"
          :class="[
            selectedId === row.id ? 'bg-indigo-100' : 'hover:bg-indigo-50',
            outcome[row.id] ? 'opacity-60' : '',
          ]"
          @click="select(row.id)"
        >
          <div class="flex items-center justify-between gap-2">
            <span class="font-medium text-gray-800 truncate">{{ row.company_name }}</span>
            <ConfidencePill :value="row.proposed_confidence" class="shrink-0" />
          </div>
          <div class="text-xs text-gray-500 truncate">
            <template v-if="outcome[row.id]">
              <span :class="outcome[row.id].tone">{{ outcome[row.id].label }}</span>
            </template>
            <template v-else-if="row.status === 'decided'">
              {{ row.decision === 'url' ? domainOf(row.final_url) : 'no website' }}
              · {{ SOURCE_LABELS[row.decision_source] || row.decision_source }}
            </template>
            <template v-else>
              {{ row.proposed_url ? domainOf(row.proposed_url) : 'nothing proposed' }}
            </template>
            <span v-if="row.injection_flags.length" class="text-amber-700"> · flagged</span>
          </div>
        </div>
        <div v-if="page < numPages" class="px-3 py-3 text-center">
          <button type="button" class="text-xs text-indigo-600 hover:underline" :disabled="loading" @click="loadMore">
            {{ loading ? 'Loading…' : 'Load more' }}
          </button>
        </div>
      </div>

      <!-- Selected row -->
      <div class="flex-1 min-w-0 flex">
        <div class="flex-1 min-w-0 flex flex-col">
          <div class="flex-1 overflow-y-auto bg-[#f6f7f9]">
            <div v-if="!selectedId" class="h-full flex items-center justify-center text-gray-400">Select a row to review.</div>
            <div v-else-if="detailError" class="p-6 text-red-600">{{ detailError }}</div>
            <div v-else-if="!detail" class="p-6 text-gray-500">Loading…</div>
            <div v-else class="p-5 max-w-3xl">
              <UrlReviewDetail
                :review="detail"
                :picked-index="pickedIndex"
                :decidable="decidable"
                @pick="pick"
              />
            </div>
          </div>

          <!-- Decision -->
          <div v-if="detail" class="border-t border-gray-300 bg-white px-5 py-3">
            <template v-if="decidable">
              <div class="flex items-center gap-2 flex-wrap">
                <span class="text-xs text-gray-500">Website:</span>
                <span v-if="pickedIndex !== null && !useOther" class="font-medium text-gray-800 break-all">{{ pickedUrl }}</span>
                <input
                  v-else
                  ref="otherInput"
                  v-model="otherUrl"
                  type="url"
                  placeholder="https://… a site you found yourself"
                  class="border border-gray-300 rounded-md px-2 py-1 grow min-w-[16rem]"
                />
                <button
                  type="button"
                  class="text-xs text-indigo-600 hover:underline"
                  @click="toggleOther"
                >{{ useOther ? 'Choose a candidate instead' : 'Enter another URL' }}</button>
              </div>
              <div class="flex items-center gap-2 mt-2">
                <button
                  type="button"
                  :disabled="!!busy || !chosenUrl"
                  @click="decideUrl"
                  class="px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                >Use this website</button>
                <button
                  type="button"
                  :disabled="!!busy"
                  @click="decideNoUrl"
                  class="px-3 py-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50"
                >No website</button>
                <button
                  type="button"
                  :disabled="!!busy"
                  @click="skip"
                  class="px-3 py-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50"
                >Skip</button>
                <button
                  type="button"
                  :disabled="!chosenUrl"
                  @click="showPreview = !showPreview"
                  class="ml-auto text-xs text-gray-600 hover:underline disabled:opacity-50"
                >{{ showPreview ? 'Hide preview' : 'Preview site' }}</button>
              </div>
            </template>

            <!-- Already decided, skipped or superseded: what happened, and the way back. -->
            <div v-else class="flex items-center gap-3 flex-wrap">
              <span class="text-gray-700">
                <template v-if="detail.status === 'decided'">
                  {{ detail.decision === 'url' ? 'Website set to' : 'Marked as having no website' }}
                  <a
                    v-if="detail.decision === 'url'"
                    :href="detail.final_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-indigo-700 hover:underline break-all"
                  >{{ detail.final_url }}</a>
                  <span class="text-gray-500">
                    · {{ SOURCE_LABELS[detail.decision_source] || detail.decision_source }}<template v-if="detail.decided_by">, {{ detail.decided_by }}</template>
                    <template v-if="detail.decided_at">, {{ formatWhen(detail.decided_at) }}</template>
                  </span>
                  <span v-if="detail.decision_reasoning" class="block text-xs text-gray-500">{{ detail.decision_reasoning }}</span>
                </template>
                <template v-else-if="detail.status === 'skipped'">Skipped.</template>
                <template v-else-if="detail.status === 'superseded'">
                  Closed: the company got a website some other way ({{ detail.company.website_url }}).
                </template>
              </span>
              <button
                v-if="detail.status === 'decided'"
                type="button"
                :disabled="!!busy"
                @click="undo(detail.id)"
                class="px-2 py-1 rounded-md text-xs border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50"
              >Undo</button>
              <button
                v-else-if="detail.status === 'skipped'"
                type="button"
                :disabled="!!busy"
                @click="restore(detail.id)"
                class="px-2 py-1 rounded-md text-xs border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50"
              >Back to the queue</button>
            </div>
          </div>
        </div>

        <!-- Preview of the chosen site. Sandboxed with no scripts, so a site
             that needs JavaScript (or refuses to be framed) shows blank; open it
             in a new tab instead. -->
        <div v-if="showPreview && chosenUrl && decidable" class="w-1/2 min-w-[24rem] border-l border-gray-300 flex flex-col bg-white">
          <div class="px-3 py-1.5 border-b border-gray-200 text-xs text-gray-500 flex justify-between gap-2">
            <span class="truncate">{{ chosenUrl }}</span>
            <a :href="chosenUrl" target="_blank" rel="noopener noreferrer" class="text-indigo-600 hover:underline shrink-0">Open in new tab ↗</a>
          </div>
          <iframe :key="chosenUrl" :src="chosenUrl" sandbox="" referrerpolicy="no-referrer" class="flex-1 w-full"></iframe>
        </div>
      </div>
    </div>

    <UrlReviewHelp v-if="helpOpen" @close="helpOpen = false" />
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'vue-toast-notification'
import {
  getUrlReviews, getUrlReview, getUrlDiscoveryRuns, decideUrlReview, skipUrlReview,
  restoreUrlReview, undoUrlReview,
} from '@/services/UrlReview'
import UrlReviewDetail from './UrlReviewDetail.vue'
import ConfidencePill from './ConfidencePill.vue'
import UrlReviewHelp from './UrlReviewHelp.vue'
import HelpSVG from '../svg-components/HelpSVG.vue'
import { getSession } from '@/services/ResearchSession'
import { googleSearchUrl } from './links'
import { visibleCandidates } from './candidates'

const store = useStore()
const $toast = useToast()

const STATUS_LABELS = {
  pending: 'Pending',
  skipped: 'Skipped',
  decided: 'Decided',
  superseded: 'Superseded',
  any: 'Any status',
}
const SOURCE_LABELS = { human: 'a person', ai: 'Claude', spreadsheet: 'spreadsheet' }
const PAGE_SIZE = 100

// Filters
const status = ref('pending')
const kind = ref('review')
const runId = ref('')
const band = ref('')
const hasCage = ref('')
const source = ref('')
const search = ref('')
const runs = ref([])
const shortcutsOn = ref(false)
const helpOpen = ref(false)

// Queue
const rows = ref([])
const page = ref(1)
const numPages = ref(1)
const count = ref(0)
const countCapped = ref(false)
const loading = ref(false)
const rowEls = {}

// Selected row
const selectedId = ref(null)
const details = reactive({})          // review id -> detail payload
const detailError = ref('')
const pickedIndex = ref(null)
const useOther = ref(false)
const otherUrl = ref('')
const otherInput = ref(null)
const showPreview = ref(false)
const busy = ref('')

// What happened to each row on this visit, for the queue: { label, tone }.
const outcome = reactive({})
// The last decision made here, for u / Undo.
const lastDecidedId = ref(null)

const detail = computed(() => (selectedId.value ? details[selectedId.value] : null))
const decidable = computed(() => !!detail.value && detail.value.status === 'pending')
const pickedUrl = computed(() => {
  if (!detail.value || pickedIndex.value === null) return ''
  const c = detail.value.candidates.find((cand) => cand.index === pickedIndex.value)
  return c ? c.url : ''
})
const chosenUrl = computed(() => (useOther.value ? otherUrl.value.trim() : pickedUrl.value))

function token() {
  return store.state.auth.token
}

function domainOf(url) {
  try { return new URL(url).hostname.replace(/^www\./, '') } catch (e) { return url }
}

function formatWhen(iso) {
  if (!iso) return '—'
  try { return new Date(iso).toLocaleString() } catch (e) { return iso }
}

function formatDay(iso) {
  if (!iso) return ''
  try { return new Date(iso).toLocaleDateString() } catch (e) { return iso }
}

function errorDetail(e, fallback) {
  return e?.response?.data?.detail || fallback
}

// -- loading -----------------------------------------------------------------

function params(toPage) {
  const p = { status: status.value, page: toPage, page_size: PAGE_SIZE }
  if (kind.value) p.kind = kind.value
  if (runId.value) p.run = runId.value
  if (band.value) {
    const [lo, hi] = band.value.split('-')
    if (lo) p.min_confidence = lo
    if (hi) p.max_confidence = hi
  }
  if (hasCage.value) p.has_cage = hasCage.value
  if (status.value === 'decided' && source.value) p.source = source.value
  if (search.value.trim()) p.q = search.value.trim()
  return p
}

// Each load's number; a slower, older one (typing in the search) mustn't
// overwrite a newer one.
let loadSeq = 0

async function load() {
  const seq = ++loadSeq
  loading.value = true
  try {
    const data = await getUrlReviews(token(), params(1))
    if (seq !== loadSeq) return
    rows.value = data.results || []
    page.value = data.page || 1
    numPages.value = data.num_pages || 1
    count.value = data.count || 0
    countCapped.value = !!data.count_is_capped
    for (const key of Object.keys(outcome)) delete outcome[key]
    lastDecidedId.value = null
    select(rows.value.length ? rows.value[0].id : null)
  } catch (e) {
    if (seq !== loadSeq) return
    rows.value = []
    $toast.error('Could not load the review queue')
  } finally {
    if (seq === loadSeq) loading.value = false
  }
}

async function loadMore() {
  if (loading.value || page.value >= numPages.value) return
  const seq = loadSeq
  loading.value = true
  try {
    const data = await getUrlReviews(token(), params(page.value + 1))
    if (seq !== loadSeq) return
    const known = new Set(rows.value.map((r) => r.id))
    rows.value.push(...(data.results || []).filter((r) => !known.has(r.id)))
    page.value = data.page || page.value + 1
    numPages.value = data.num_pages || numPages.value
  } catch (e) {
    $toast.error('Could not load more rows')
  } finally {
    if (seq === loadSeq) loading.value = false
  }
}

async function fetchDetail(id) {
  if (details[id]) return details[id]
  const data = await getUrlReview(id, token())
  details[id] = data
  return data
}

async function select(id) {
  selectedId.value = id
  detailError.value = ''
  pickedIndex.value = null
  useOther.value = false
  otherUrl.value = ''
  if (!id) return
  nextTick(() => rowEls[id]?.scrollIntoView({ block: 'nearest' }))
  try {
    const d = await fetchDetail(id)
    if (selectedId.value !== id) return
    // Start on the run's proposal, so accepting it is one key.
    const proposed = visibleCandidates(d).find((c) => c.url === d.proposed_url)
    pickedIndex.value = proposed ? proposed.index : null
  } catch (e) {
    if (selectedId.value === id) detailError.value = errorDetail(e, 'Could not load this row')
    return
  }
  // Have the next one ready.
  const next = neighbour(1)
  if (next) fetchDetail(next).catch(() => {})
}

// The row `step` places away from the selected one.
function neighbour(step) {
  const i = rows.value.findIndex((r) => r.id === selectedId.value)
  const row = rows.value[i + step]
  return row ? row.id : null
}

async function move(step) {
  const target = neighbour(step)
  if (target) {
    select(target)
  } else if (step > 0 && page.value < numPages.value) {
    await loadMore()
    const next = neighbour(1)
    if (next) select(next)
  }
}

// After deciding, go on to the next row nobody has acted on this visit.
async function advance() {
  let i = rows.value.findIndex((r) => r.id === selectedId.value) + 1
  let more = true
  while (more) {
    for (; i < rows.value.length; i += 1) {
      if (!outcome[rows.value[i].id]) return select(rows.value[i].id)
    }
    const before = rows.value.length
    if (page.value < numPages.value) await loadMore()
    more = rows.value.length > before
  }
}

let searchTimer = null
watch([status, kind, runId, band, hasCage, source], () => load())
watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(load, 300)
})

async function loadRuns() {
  try {
    runs.value = await getUrlDiscoveryRuns(token())
  } catch (e) {
    // Without it there's just no run filter.
  }
}

// -- choosing ----------------------------------------------------------------

function pick(index) {
  pickedIndex.value = index
  useOther.value = false
}

function toggleOther() {
  useOther.value = !useOther.value
  if (useOther.value) nextTick(() => otherInput.value?.focus())
}

// -- deciding ----------------------------------------------------------------

function settle(id, review, label, tone = 'text-gray-600') {
  details[id] = review
  outcome[id] = { label, tone }
  const row = rows.value.find((r) => r.id === id)
  if (row) Object.assign(row, { status: review.status, decision: review.decision, final_url: review.final_url })
}

async function act(kindOfAct, call) {
  const id = selectedId.value
  busy.value = kindOfAct
  try {
    const review = await call(id)
    return { id, review }
  } catch (e) {
    const data = e?.response?.data || {}
    if (data.code === 'superseded') {
      // Someone (or something) gave the company a website first; the row is closed.
      details[id] = await getUrlReview(id, token()).catch(() => details[id])
      outcome[id] = { label: 'already had a website', tone: 'text-gray-500' }
      $toast.info(data.detail || 'This company already has a website; nothing was changed')
      advance()
    } else {
      $toast.error(errorDetail(e, 'Could not save that'))
    }
    return null
  } finally {
    busy.value = ''
  }
}

// A person's decision is yes or no, so it carries no confidence or note;
// Claude's (through the MCP tools) carry both.
async function decideUrl() {
  if (!decidable.value || !chosenUrl.value || busy.value) return
  const body = useOther.value
    ? { decision: 'url', url: chosenUrl.value }
    : { decision: 'url', candidate_index: pickedIndex.value }
  const done = await act('deciding', (id) => decideUrlReview(id, body, token()))
  if (!done) return
  settle(done.id, done.review, `✓ ${domainOf(done.review.final_url)}`, 'text-green-700')
  lastDecidedId.value = done.id
  advance()
}

async function decideNoUrl() {
  if (!decidable.value || busy.value) return
  const done = await act('deciding', (id) => decideUrlReview(id, { decision: 'no_url' }, token()))
  if (!done) return
  settle(done.id, done.review, '✗ no website')
  lastDecidedId.value = done.id
  advance()
}

async function skip() {
  if (!decidable.value || busy.value) return
  const done = await act('skipping', (id) => skipUrlReview(id, token()))
  if (!done) return
  settle(done.id, done.review, 'skipped', 'text-gray-500')
  advance()
}

async function undo(id) {
  if (!id || busy.value) return
  busy.value = 'undoing'
  try {
    const review = await undoUrlReview(id, token())
    details[id] = review
    delete outcome[id]
    const row = rows.value.find((r) => r.id === id)
    if (row) Object.assign(row, { status: review.status, decision: '', final_url: '' })
    if (lastDecidedId.value === id) lastDecidedId.value = null
    select(id)
  } catch (e) {
    $toast.error(errorDetail(e, 'Could not undo'))
  } finally {
    busy.value = ''
  }
}

async function restore(id) {
  if (!id || busy.value) return
  busy.value = 'restoring'
  try {
    const review = await restoreUrlReview(id, token())
    details[id] = review
    delete outcome[id]
    const row = rows.value.find((r) => r.id === id)
    if (row) row.status = review.status
    select(id)
  } catch (e) {
    $toast.error(errorDetail(e, 'Could not put it back'))
  } finally {
    busy.value = ''
  }
}

// -- keyboard ----------------------------------------------------------------

function onKey(e) {
  if (!shortcutsOn.value || helpOpen.value || e.metaKey || e.ctrlKey || e.altKey) return
  const tag = (e.target && e.target.tagName) || ''
  if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) || e.target?.isContentEditable) return
  const key = e.key
  if (key === 'j' || key === 'ArrowDown') { e.preventDefault(); move(1) }
  else if (key === 'k' || key === 'ArrowUp') { e.preventDefault(); move(-1) }
  else if (/^[1-9]$/.test(key) && decidable.value) {
    // The number shown beside each candidate: its place in the visible list.
    const c = visibleCandidates(detail.value)[Number(key) - 1]
    if (c) pick(c.index)
  }
  else if (key === 'a') decideUrl()
  else if (key === 'n') decideNoUrl()
  else if (key === 's') skip()
  else if (key === 'u') undo(lastDecidedId.value)
  else if (key === 'p' && chosenUrl.value) showPreview.value = !showPreview.value
  else if (key === 'o' && chosenUrl.value) window.open(chosenUrl.value, '_blank', 'noopener,noreferrer')
  else if (key === 'g' && detail.value) window.open(googleSearchUrl(detail.value.company), '_blank', 'noopener,noreferrer')
}

const hasActiveResearchSession = computed(() => store.state.research.hasActiveResearchSession)

async function loadOpenSession() {
  try {
    const result = await getSession(store.state.auth.token, 'url_review')
    store.dispatch(result.data.session ? 'research/setHasActiveResearchSessionTrue' : 'research/setHasActiveResearchSessionFalse')
  } catch (e) {
    // The indicator is a convenience; the page works without it.
  }
}

onMounted(() => {
  window.addEventListener('keydown', onKey)
  load()
  loadRuns()
  loadOpenSession()
})
onBeforeUnmount(() => window.removeEventListener('keydown', onKey))
</script>
